import {
	CompetencyFeedbackScore,
	JobTitleCompetenciesPartialFragment,
	UserCompetenciesPartialFragment,
	UserGrowthPathFeedbackQuery,
} from '@treams/treams-network';

import { GrowthPathReceivedFeedbackList } from './components/GrowthPathCompetenciesList';
import { GrowthPathScale, growthPathScaleLevels } from './hooks/useGrowthPathScale';
import { GrowthPathAppraisal, GrowthPathJobCompetency, GrowthPathJobTitle } from './types';

export interface GrowthPathData {
	jobTitle?: GrowthPathJobTitle;
	appraisals: GrowthPathAppraisal[];
	jobCompetencies: GrowthPathJobCompetency[];
	hasCompetenciesWithoutLabel: boolean;
}

/**
 * On a Groeipad scale every job competency is scored on the scale's levels and aims for its
 * target, instead of the competency's own levels and the level the job asks for.
 * @param jobCompetencies The job competencies as the job title defines them.
 * @param scale The workspace Groeipad scale, if any; without one the input is returned as is.
 */
export function onGrowthPathScale<
	T extends { competencyLevel: number; competency: { levels: unknown[] } },
>(jobCompetencies: T[], scale: GrowthPathScale | undefined): T[] {
	if (!scale) {
		return jobCompetencies;
	}

	const levels = growthPathScaleLevels(scale);

	return jobCompetencies.map((jobCompetency) => ({
		...jobCompetency,
		competencyLevel: scale.targetLevelIndex,
		competency: { ...jobCompetency.competency, levels },
	}));
}

export function growthPathJobPositions(user: UserCompetenciesPartialFragment) {
	return user.jobPositions.filter((jobPosition) => !jobPosition.jobTitle.excludedFromGrowthPath);
}

export function growthPathDataMappingFromUser(
	user: UserCompetenciesPartialFragment,
	jobTitleID: string | undefined,
	scale: GrowthPathScale | undefined,
): GrowthPathData {
	const jobPositions = growthPathJobPositions(user);

	const selectedPositions = jobPositions.filter((jobPosition) => {
		if (!jobTitleID || jobTitleID === 'all') {
			return true;
		}
		return jobPosition.jobTitle.id === jobTitleID;
	});

	// Each job competency keeps the appraisal from its own job position. A job-specific
	// competency is only scored for one job title, so pairing per position stops a score
	// given for one title from showing up under another that lists the same competency.
	const jobCompetencies = selectedPositions.flatMap((jobPosition) =>
		onGrowthPathScale(jobPosition.competencies, scale).map((jobCompetency) => ({
			...jobCompetency,
			jobTitleID: jobPosition.jobTitle.id,
			appraisal: jobPosition.appraisal.find(
				(appraisal) => appraisal.competency.id === jobCompetency.competency.id,
			),
		})),
	);

	const appraisals = selectedPositions.flatMap(({ appraisal }) => appraisal);

	const hasCompetenciesWithoutLabel = jobCompetencies.some(
		(jobCompetency) => jobCompetency.competency.label === null,
	);

	return {
		appraisals,
		jobCompetencies,
		jobTitle: jobTitleID
			? jobPositions.find((jobPosition) => {
					return jobPosition.jobTitle.id === jobTitleID;
				})?.jobTitle
			: undefined,
		hasCompetenciesWithoutLabel,
	};
}

export function growthPathDataMappingFromJobTitle(
	jobTitle: JobTitleCompetenciesPartialFragment | undefined,
	scale: GrowthPathScale | undefined,
): GrowthPathData {
	const appraisals = jobTitle?.appraisal ?? [];
	const jobCompetencies = onGrowthPathScale(jobTitle?.competencies ?? [], scale).map(
		(jobCompetency) => ({
			...jobCompetency,
			jobTitleID: jobTitle?.id,
			appraisal: appraisals.find(
				(appraisal) => appraisal.competency.id === jobCompetency.competency.id,
			),
		}),
	);
	const hasCompetenciesWithoutLabel = jobCompetencies.some(
		(jobCompetency) => jobCompetency.competency.label === null,
	);

	return {
		appraisals,
		jobCompetencies,
		jobTitle,
		hasCompetenciesWithoutLabel,
	};
}

export function receivedFeedbackFromUser(
	user: UserGrowthPathFeedbackQuery['user'],
): GrowthPathReceivedFeedbackList {
	const scoresByJobCompetencyId: Record<string, CompetencyFeedbackScore[] | null> = {};

	for (const jobPosition of user.jobPositions) {
		if (jobPosition.jobTitle.excludedFromGrowthPath) {
			continue;
		}

		for (const jobCompetency of jobPosition.competencies) {
			const appraisal = jobPosition.appraisal.find(
				(row) => row.competency.id === jobCompetency.competency.id,
			);

			scoresByJobCompetencyId[jobCompetency.id] =
				appraisal?.feedbackScores === null
					? null
					: ((appraisal?.feedbackScores ?? []) as CompetencyFeedbackScore[]);
		}
	}

	return { scoresByJobCompetencyId };
}

export function unavailableFeedback(
	user: UserCompetenciesPartialFragment,
): GrowthPathReceivedFeedbackList {
	const scoresByJobCompetencyId: Record<string, null> = {};

	for (const jobPosition of growthPathJobPositions(user)) {
		for (const jobCompetency of jobPosition.competencies) {
			scoresByJobCompetencyId[jobCompetency.id] = null;
		}
	}

	return { scoresByJobCompetencyId };
}
